import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import feathersClient from '../../../../../src/apiClient';
import { useMutation, useQuery } from 'react-query';
import { ModalShell } from '@tymbe/ty-components/modals';
import { CrossIcon } from '@tymbe/ty-components/icons';
import { SecondaryButton } from '@tymbe/ui-primitives';
import { Form, FormApi, FormState } from 'informed';
import { TyTextArea } from '@tymbe/ty-components/inputs';
import { ErrorAlert, SuccessAlert } from '../../../../../src/components/alerts';
import AlertModal from '@tymbe/ty-components/modals/AlertModal';
import { PersonData, PersonDataData } from '../../../../types/TymbeApi';
import { LifeTransactionType } from '@tymbe/schema/enums';
import { TyLifeScore } from './TyLivesScore';
import { useLifeTransactions } from '../components/useLifeTransactions';
import Spinner from '@tymbe/ty-components/indicators/Spinner';
import { useUser } from '../../../../apiClient/ApiContext';
import { Divider } from '@tymbe/ty-components/indicators';
import { TyLivesFormInput } from './TyLivesFormInput';
import { TyLivesConsequenceInfo } from './TyLivesConsequenceInfo';

type FormValues = {
  lives: number;
  note: string;
};

export const TyLivesModal = ({
  isOpen,
  toggleIsOpen,
}: {
  isOpen: boolean;
  toggleIsOpen: () => void;
}) => {
  const user = useUser();
  const { personId } = useParams();
  const { refetch } = useLifeTransactions(personId!);

  const [existingNumberOfLives, setExistingNumberOfLives] = useState<number>(0);
  const [newNumberOfLives, setNewNumberOfLives] = useState<number>(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const formApiRef = useRef<FormApi<FormValues>>(undefined);

  const getPerson = async (): Promise<PersonData | void> => {
    if (!personId) {
      ErrorAlert('Uživatelské ID nenalezeno');
      return Promise.resolve();
    }
    return await feathersClient
      .service('person')
      .get(personId, { query: { $eager: 'accountState' } });
  };

  const getPersonData = async (): Promise<PersonDataData> => {
    if (!personId) {
      ErrorAlert('Uživatelské ID nenalezeno');
      return await new Promise(() => {
        return;
      });
    }
    return await feathersClient.service('person-data').get(personId);
  };

  const personDataQuery = useQuery({
    queryKey: ['person-data', personId],
    queryFn: getPersonData,
  });

  const personQuery = useQuery({
    queryKey: ['person', personId],
    queryFn: getPerson,
  });

  useEffect(() => {
    personQuery.refetch();
    personDataQuery.refetch().then((data) => {
      if (data && isOpen === true) {
        setExistingNumberOfLives(data.data?.lives ?? 0);
        formApiRef.current?.setValues({ lives: data.data?.lives ?? 0 });
      }
    });
  }, [isOpen]);

  const addLifeMutation = useMutation(
    ['addLifeTransaction'],
    async ({ lives, note }: FormValues) => {
      if (!personDataQuery.data) {
        ErrorAlert('Chybí data uživatele');
        personDataQuery.refetch();
        return;
      }

      const newLivesValue = -(personDataQuery.data.lives - lives);
      await feathersClient.service('life-transaction').create({
        type: LifeTransactionType.MANUAL,
        person_id: Number.parseInt(personId!),
        created_by: user.id,
        note: note,
        value: newLivesValue,
      });
    },
    {
      onSuccess: () => {
        refetch();
        setShowConfirmDialog(false);
        toggleIsOpen();
        SuccessAlert('Životy upraveny');
      },
      onError: () => {
        ErrorAlert('Něco se pokazilo');
      },
    }
  );

  return (
    <ModalShell
        showModal={isOpen}
        onClose={toggleIsOpen}
        className="min-w-[500px]"
        overlayClassName="z-10"
      >
        <Form<FormValues>
          onSubmit={(values) => addLifeMutation.mutate(values.values)}
          formApiRef={formApiRef}
        >
          <AlertModal
            title="Opravdu chcete upravit životy?"
            confirmButtonText="Ano"
            cancelButtonText="Ne"
            showModal={showConfirmDialog}
            onConfirm={() => {
              formApiRef.current?.submitForm();
              setShowConfirmDialog(false);
            }}
            onCancel={() => setShowConfirmDialog(false)}
            overlayClassName="z-modal"
          />

          <div className="flex flex-row justify-between px-4 pt-4">
            <span className="text-heading-4-xl">Upravit počet srdíček</span>
            <CrossIcon className="cursor-pointer" onClick={toggleIsOpen} />
          </div>
          <Divider />

          <div className="flex flex-col px-4 gap-4">
            <div>
              <span className="text-caption-md">Původní stav</span>
              <div className="flex gap-6">
                <TyLifeScore lives={existingNumberOfLives} />
                <span className="text-body-2-md w-[148px] text-center">
                  {existingNumberOfLives}
                </span>
              </div>
            </div>
            <div>
              <span className="text-caption-md">Nový stav</span>
              <TyLivesFormInput
                name="lives"
                onChange={(state) => setNewNumberOfLives(state.value)}
              />
            </div>
            <TyLivesConsequenceInfo
              originalLives={existingNumberOfLives}
              newLives={newNumberOfLives}
            />
            <div>
              <span className="text-caption-md">Poznámka</span>
              <TyTextArea name="note" rows={5} />
            </div>
          </div>
          <div className="flex flex-row p-4">
            <SecondaryButton
              disabled={
                addLifeMutation.isLoading ||
                personDataQuery.data?.lives === newNumberOfLives
              }
              className="ml-auto"
              onClick={() => setShowConfirmDialog(true)}
              type="button"
            >
              {addLifeMutation.isLoading ? (
                <Spinner className="w-[20px] h-[20px]" />
              ) : (
                'Uložit'
              )}
            </SecondaryButton>
          </div>
        </Form>
      </ModalShell>
  );
};
